import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Button, AppBar, Toolbar, IconButton,
  Avatar, Grid, Card, CardContent, CardActions, Chip, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, CircularProgress, Divider
} from '@mui/material';
import { ref, onValue, get } from 'firebase/database';
import { database, auth } from '../firebase/Config';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Subscription = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    // Carregar planos ativos
    const plansRef = ref(database, 'plans');
    const unsubscribePlans = onValue(plansRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const plansList = Object.keys(data)
          .map(key => ({ id: key, ...data[key] }))
          .filter(plan => plan.active);
        setPlans(plansList);
      }
    });

    // Carregar assinatura atual do usuário
    if (auth.currentUser) {
      const subscriptionRef = ref(database, `subscriptions/${auth.currentUser.uid}`);
      const unsubscribeSubscription = onValue(subscriptionRef, (snapshot) => {
        const data = snapshot.val();
        setCurrentSubscription(data);
      });

      return () => {
        unsubscribePlans();
        unsubscribeSubscription();
      };
    }

    return () => unsubscribePlans();
  }, []);

  const getDurationText = (duration, type) => {
    const typeText = {
      days: duration === 1 ? 'dia' : 'dias',
      months: duration === 1 ? 'mês' : 'meses',
      years: duration === 1 ? 'ano' : 'anos'
    };
    return `${duration} ${typeText[type]}`;
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setOpenDialog(true);
  };

  const handleConfirmSubscription = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    try {
      const functions = getFunctions();
      const createSubscription = httpsCallable(functions, 'criarAssinatura');
      
      const result = await createSubscription({
        planId: selectedPlan.id,
        userId: auth.currentUser.uid
      });

      if (result.data.success) {
        // Redirecionar para o link de pagamento do Mercado Pago
        window.open(result.data.paymentUrl, '_blank');
        setAlert({ 
          show: true, 
          message: 'Redirecionando para pagamento... Após o pagamento, sua assinatura será ativada automaticamente.', 
          severity: 'success' 
        });
        setOpenDialog(false);
      } else {
        throw new Error(result.data.error || 'Erro ao criar assinatura');
      }
    } catch (error) {
      console.error('Erro ao criar assinatura:', error);
      setAlert({ 
        show: true, 
        message: 'Erro ao processar assinatura. Tente novamente.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar sua assinatura? Suas licenças serão bloqueadas.')) {
      return;
    }

    setLoading(true);
    try {
      const functions = getFunctions();
      const cancelSubscription = httpsCallable(functions, 'cancelarAssinatura');
      
      const result = await cancelSubscription({
        userId: auth.currentUser.uid
      });

      if (result.data.success) {
        setAlert({ 
          show: true, 
          message: 'Assinatura cancelada com sucesso. Suas licenças foram bloqueadas.', 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('Erro ao cancelar assinatura:', error);
      setAlert({ 
        show: true, 
        message: 'Erro ao cancelar assinatura. Tente novamente.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const isSubscriptionActive = () => {
    if (!currentSubscription) return false;
    if (currentSubscription.status !== 'active') return false;
    
    const expiryDate = new Date(currentSubscription.expiresAt);
    return expiryDate > new Date();
  };

  const getExpiryDate = () => {
    if (!currentSubscription?.expiresAt) return null;
    return new Date(currentSubscription.expiresAt).toLocaleDateString('pt-BR');
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        boxShadow: '0 3px 5px 2px rgba(139, 92, 246, .3)'
      }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ bgcolor: '#7c3aed', mr: 2 }}>
            <PaymentIcon />
          </Avatar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Assinatura
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {alert.show && (
          <Alert 
            severity={alert.severity} 
            onClose={() => setAlert({ ...alert, show: false })}
            sx={{ mb: 3 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* Status da Assinatura Atual */}
        {currentSubscription && (
          <Paper elevation={3} sx={{ p: 3, mb: 4, borderLeft: `6px solid ${isSubscriptionActive() ? '#10b981' : '#ef4444'}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: isSubscriptionActive() ? '#10b981' : '#ef4444' }} />
                  Assinatura {isSubscriptionActive() ? 'Ativa' : currentSubscription.status === 'cancelled' ? 'Cancelada' : 'Expirada'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Plano: <strong>{currentSubscription.planName}</strong>
                </Typography>
                {isSubscriptionActive() && (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Válido até: <strong>{getExpiryDate()}</strong>
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                <Chip 
                  label={
                    isSubscriptionActive() ? 'ATIVO' : 
                    currentSubscription.status === 'cancelled' ? 'CANCELADO' : 
                    'EXPIRADO'
                  }
                  color={
                    isSubscriptionActive() ? 'success' : 
                    currentSubscription.status === 'cancelled' ? 'warning' : 
                    'error'
                  }
                  sx={{ fontWeight: 'bold' }}
                />
                {isSubscriptionActive() && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleCancelSubscription}
                    disabled={loading}
                    sx={{ mt: 1 }}
                  >
                    Cancelar Assinatura
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        )}

        {/* Título dos Planos */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1f2937', mb: 1 }}>
            Escolha seu Plano
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Selecione o plano que melhor atende suas necessidades
          </Typography>
        </Box>

        {/* Grid de Planos */}
        <Grid container spacing={3}>
          {plans.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  Nenhum plano disponível no momento.
                </Typography>
              </Paper>
            </Grid>
          ) : (
            plans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.id}>
                <Card 
                  elevation={3}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(139, 92, 246, 0.3)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 3 }}>
                    <Avatar sx={{ bgcolor: '#8b5cf6', width: 56, height: 56, mx: 'auto', mb: 2 }}>
                      <StarIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: '#1f2937' }}>
                      {plan.name}
                    </Typography>
                    
                    {plan.description && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, minHeight: 40 }}>
                        {plan.description}
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ my: 3 }}>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#8b5cf6' }}>
                        R$ {plan.price.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        por {getDurationText(plan.duration, plan.durationType)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
                      <AccessTimeIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                      <Typography variant="body2" color="textSecondary">
                        Renovação automática de licença
                      </Typography>
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={() => handleSelectPlan(plan)}
                      disabled={currentSubscription?.planId === plan.id && isSubscriptionActive()}
                      sx={{
                        bgcolor: '#8b5cf6',
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        '&:hover': { bgcolor: '#7c3aed' },
                        '&:disabled': { bgcolor: '#e5e7eb' }
                      }}
                    >
                      {currentSubscription?.planId === plan.id && isSubscriptionActive() 
                        ? 'Plano Atual' 
                        : 'Assinar Agora'
                      }
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Container>

      {/* Dialog de Confirmação */}
      <Dialog open={openDialog} onClose={() => !loading && setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f3e8ff', color: '#8b5cf6' }}>
          Confirmar Assinatura
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {selectedPlan && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedPlan.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Valor: <strong>R$ {selectedPlan.price.toFixed(2)}</strong>
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Duração: <strong>{getDurationText(selectedPlan.duration, selectedPlan.durationType)}</strong>
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="textSecondary">
                Você será redirecionado para o Mercado Pago para concluir o pagamento.
                Sua licença será renovada automaticamente após a confirmação do pagamento.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmSubscription}
            variant="contained"
            disabled={loading}
            sx={{ 
              bgcolor: '#8b5cf6',
              '&:hover': { bgcolor: '#7c3aed' },
              minWidth: 120
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Subscription;
